import React, { useState, useEffect } from 'react';
import { Modal, Button, Label } from '../ui';

// CapacityPickerModal
// ─────────────────────────────────────────────────────────────────────
// Generic preset-driven modal for setting a capacity value (seats /
// storage base / storage add-on). Replaces the previous prompt() ux
// — a dropdown of sensible preset tiers + an "Unlimited" option for
// caps that support it + a "Custom" option that falls back to a
// numeric input for one-off deals.
//
// Three modes wired via props:
//   variant="seat"          → integers (seat count)
//   variant="storage-base"  → GB, can be null = unlimited
//   variant="storage-addon" → GB, must be > 0 (stacks; "Unlimited"
//                             isn't a valid concept for an add-on)
//
// Props:
//   open              — boolean
//   onClose           — fn
//   variant           — 'seat' | 'storage-base' | 'storage-addon'
//   currentValue      — number | null  (the existing value; pre-selects)
//   currentAddon      — number         (storage-addon: existing addon, MB)
//   onSubmit(value)   — async fn called with the chosen value
//                       (number, or null for unlimited)
//   working           — boolean → disables submit while in-flight

// Preset tiers per variant. Curated to match the pricing tiers from
// PAYSTACK_SETUP.md so super-admin choices stay aligned with what
// customers actually buy. "Custom" lets you type any value for
// one-off deals.
const PRESETS = {
  seat: [
    { value: 1,    label: '1 seat (solo)' },
    { value: 3,    label: '3 seats' },
    { value: 5,    label: '5 seats (Basic)' },
    { value: 10,   label: '10 seats' },
    { value: 15,   label: '15 seats (Pro)' },
    { value: 25,   label: '25 seats' },
    { value: 50,   label: '50 seats' },
    { value: 100,  label: '100 seats' },
    { value: null, label: 'Unlimited (enterprise)' },
  ],
  'storage-base': [
    // GB values — converted to MB before submitting.
    { value: 1,    label: '1 GB' },
    { value: 5,    label: '5 GB' },
    { value: 10,   label: '10 GB (Basic default)' },
    { value: 25,   label: '25 GB' },
    { value: 50,   label: '50 GB (Pro default)' },
    { value: 100,  label: '100 GB' },
    { value: 200,  label: '200 GB' },
    { value: 500,  label: '500 GB' },
    { value: 1024, label: '1 TB' },
    { value: null, label: 'Unlimited (enterprise)' },
  ],
  'storage-addon': [
    { value: 10,   label: '+10 GB' },
    { value: 25,   label: '+25 GB' },
    { value: 50,   label: '+50 GB' },
    { value: 100,  label: '+100 GB' },
    { value: 200,  label: '+200 GB' },
    { value: 500,  label: '+500 GB' },
    { value: 1024, label: '+1 TB' },
  ],
};

const TITLES = {
  seat:            'Set seat limit',
  'storage-base':  'Set base storage limit',
  'storage-addon': 'Add storage add-on',
};

const HELP = {
  seat:
    'Number of paid seats this firm has. Used + pending invites must stay at or below this number.',
  'storage-base':
    'Base storage capacity from the plan. Stacks with any purchased add-on for the effective limit.',
  'storage-addon':
    'Stacks on top of the base plan capacity. Buying 50 GB twice = 100 GB total add-on.',
};

const CUSTOM_VALUE_SENTINEL = '__custom__';

export default function CapacityPickerModal({
  open, onClose, variant, currentValue, currentAddon, onSubmit, working,
}) {
  const presets = PRESETS[variant] || [];

  // The select stores stringified values because <select> values are
  // strings. We map back to number/null on submit.
  //   - 'null' → null (unlimited)
  //   - CUSTOM_VALUE_SENTINEL → custom numeric input mode
  //   - otherwise → Number(value)
  const initialSelect = (() => {
    if (currentValue === null || currentValue === undefined) {
      // Storage-addon doesn't have a null option; default to the
      // first preset for that variant.
      if (variant === 'storage-addon') return String(presets[0]?.value ?? '');
      return 'null';
    }
    // If the current value matches a preset, select it; else custom.
    const match = presets.find((p) => p.value === currentValue);
    return match ? String(match.value) : CUSTOM_VALUE_SENTINEL;
  })();

  const [selected, setSelected] = useState(initialSelect);
  const [custom,   setCustom]   = useState(
    initialSelect === CUSTOM_VALUE_SENTINEL && currentValue != null
      ? String(currentValue)
      : '',
  );

  // Reset state when the modal is reopened for a fresh action.
  useEffect(() => {
    if (open) {
      setSelected(initialSelect);
      setCustom(
        initialSelect === CUSTOM_VALUE_SENTINEL && currentValue != null
          ? String(currentValue)
          : '',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resolveValue = () => {
    if (selected === 'null') return null;
    if (selected === CUSTOM_VALUE_SENTINEL) {
      const n = parseFloat(custom);
      if (!Number.isFinite(n) || n < (variant === 'storage-addon' ? 0.01 : 0)) {
        return undefined; // invalid — caller should treat as "no submit"
      }
      return n;
    }
    return Number(selected);
  };

  const handleSubmit = async () => {
    const v = resolveValue();
    if (v === undefined) return; // invalid custom value, do nothing
    await onSubmit(v);
  };

  // Preview of the effective storage limit after this change — only
  // shown when variant deals with storage caps so super-admin sees
  // what the firm will actually have.
  const previewLine = (() => {
    if (variant === 'storage-base') {
      const v = resolveValue();
      if (v === undefined) return null;
      const baseGb = v == null ? null : v;
      const addonGb = (currentAddon || 0) / 1024;
      if (baseGb == null) {
        return 'Effective limit will be unlimited (add-ons ignored for unlimited plans).';
      }
      return `Effective limit will be ${(baseGb + addonGb).toFixed(1)} GB (base ${baseGb.toFixed(1)} GB${addonGb > 0 ? ` + add-on ${addonGb.toFixed(1)} GB` : ''}).`;
    }
    if (variant === 'storage-addon') {
      const v = resolveValue();
      if (v === undefined) return null;
      const addedGb       = v;
      const existingGb    = (currentAddon || 0) / 1024;
      const newAddonGb    = existingGb + addedGb;
      return `Add-on will become ${newAddonGb.toFixed(1)} GB (currently ${existingGb.toFixed(1)} GB).`;
    }
    return null;
  })();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={TITLES[variant] || 'Set value'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={working || resolveValue() === undefined}>
            {working
              ? 'Saving…'
              : variant === 'storage-addon' ? 'Add storage' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-400">{HELP[variant]}</p>

        <div>
          <Label>Choose a preset</Label>
          {/* Native <select> because the UI library's <Select> reads
              from an `options` prop and ignores children, which
              wouldn't accommodate the Custom… sentinel option below. */}
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={working}
            className="block w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 pr-8 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {presets.map((p) => (
              <option key={String(p.value)} value={p.value === null ? 'null' : String(p.value)}>
                {p.label}
              </option>
            ))}
            <option value={CUSTOM_VALUE_SENTINEL}>Custom…</option>
          </select>
        </div>

        {selected === CUSTOM_VALUE_SENTINEL && (
          <div>
            <Label>
              Custom {variant === 'seat' ? 'seat count' : 'GB'}
            </Label>
            <input
              type="number"
              min={variant === 'storage-addon' ? 0.01 : 0}
              step={variant === 'seat' ? 1 : 0.1}
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              disabled={working}
              autoFocus
              className="block w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={variant === 'seat' ? 'e.g. 12' : 'e.g. 75'}
            />
          </div>
        )}

        {previewLine && (
          <div className="rounded-lg bg-slate-100 dark:bg-slate-800 px-3 py-2 text-xs text-slate-700 dark:text-slate-300">
            {previewLine}
          </div>
        )}
      </div>
    </Modal>
  );
}
